import { memo } from 'react';
import { ArrowUpRight } from 'lucide-react';

import { SignalBadge } from '@/components/SignalBadge';
import type { EnrichedOpportunity } from '@/models/marketData';
import { formatCurrency, formatNumber, formatPercent } from '@/utils/formatters';

export const TopOpportunities = memo(
  ({
    opportunities,
    onSelect,
  }: {
    opportunities: EnrichedOpportunity[];
    onSelect: (opportunity: EnrichedOpportunity) => void;
  }) => {
    const leaders = [...opportunities]
      .sort(
        (a, b) =>
          b.score - a.score ||
          b.confidence - a.confidence ||
          b.metrics.target1RiskReward - a.metrics.target1RiskReward,
      )
      .slice(0, 4);

    return (
      <section className="panel top-opportunities">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Priority queue</span>
            <h2>Top-ranked opportunities</h2>
          </div>
          <span>Score · confidence · risk/reward</span>
        </div>
        <div className="leader-grid">
          {leaders.map((item, index) => (
            <button
              type="button"
              className="leader-card"
              key={item.tradeInstrument.instrumentKey}
              onClick={() => onSelect(item)}
              aria-label={`Open ${item.tradeInstrument.symbol} details`}
            >
              <div className="leader-card__rank">0{index + 1}</div>
              <div className="leader-card__head">
                <div>
                  <strong>{item.tradeInstrument.symbol}</strong>
                  <span>{item.tradeInstrument.exchange}</span>
                </div>
                <SignalBadge signal={item.signal} />
              </div>
              <div className="leader-card__score">{formatNumber(item.score, 0)}</div>
              <div className="leader-card__label">COMPOSITE SCORE</div>
              <div className="leader-card__stats">
                <span>
                  Watch zone <b>{formatCurrency(item.suggestedEntry)}</b>
                </span>
                <span>
                  Scenario 1 <b>+{formatPercent(item.metrics.target1UpsidePercent)}</b>
                </span>
                <span>
                  R:R <b>{formatNumber(item.metrics.target1RiskReward, 2)}×</b>
                </span>
              </div>
              <div className="quality-track" aria-label="Technical quality">
                {[
                  item.indicators.superTrendBullish,
                  item.metrics.emaAligned,
                  item.indicators.adx14 >= 25,
                  item.indicators.volumeRatio20 >= 1.5,
                ].map((active, qualityIndex) => (
                  <i className={active ? 'is-active' : ''} key={qualityIndex} />
                ))}
              </div>
              <ArrowUpRight className="leader-card__arrow" size={17} aria-hidden="true" />
            </button>
          ))}
        </div>
      </section>
    );
  },
);
